import { EditorType } from '@/components/tiptap/types/index.enums';
import BlockEditorMarkdown from '@/components/tiptap/vMarkdown/BlockEditor/block-editor';
import { useBlockEditorMarkdown } from '@/components/tiptap/vMarkdown/useBlockEditor';
import { simpleHtmlToMarkdown } from '@/helpers/htmlToMarkdown';
import { simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import { useSectionsWrappingEnabled } from '@/hooks/useMarkdownSectionalEditorFlags';
import { type TemplateEditorTabViewProps } from '@heidi/growth';
import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Legacy (vMarkdown) onboarding template editor: markdown in through the regex
 * HTML converter, HTML back out through its inverse. Retires with the
 * `release-scribe-enable-markdown-native-editor` rollout.
 */
export function TemplateEditorTabLegacy({
  content,
  onContentChange,
  tabType,
  placeholder,
  isEditable = false,
}: TemplateEditorTabViewProps) {
  const sectionsWrappingEnabled = useSectionsWrappingEnabled();
  const htmlContent = useMemo(() => {
    return simpleMarkdownToHtml({
      markdown: content,
      options: { wrapSections: sectionsWrappingEnabled },
    });
  }, [content, sectionsWrappingEnabled]);

  const { editor } = useBlockEditorMarkdown({
    content: htmlContent,
    editable: isEditable,
    placeholder,
    enableSections: true,
    surface: 'template',
    isTemplateEditor: true,
  });

  const editorRef = useRef<HTMLDivElement>(null);

  const handleOnUpdate = useCallback(() => {
    if (!editor || !onContentChange) return;

    onContentChange(
      simpleHtmlToMarkdown({
        html: editor.getHTML() ?? '',
        options: { wrapSections: sectionsWrappingEnabled },
      })
    );
  }, [editor, onContentChange, sectionsWrappingEnabled]);

  useEffect(() => {
    if (!editor) return;

    editor.on('update', handleOnUpdate);
    return () => {
      editor.off('update', handleOnUpdate);
    };
  }, [editor, handleOnUpdate]);

  if (!editor) {
    return (
      <div className="flex size-full items-center justify-center">
        <div className="size-6 animate-spin rounded-full border-2 border-border border-t-text-primary" />
      </div>
    );
  }

  return (
    <div className="size-full overflow-y-auto pr-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <BlockEditorMarkdown
        editor={editor}
        ref={editorRef}
        editorType={EditorType.TEMPLATE}
        enableBubbleMenu
        enableFindReplace={false}
        enableSnippet={false}
        tabType={undefined}
        testTag={`onboarding-${tabType}-block-editor`}
        className="!overflow-visible"
      />
    </div>
  );
}
